/**
 * Per-programme content for the five Focus Area pages. Everything that differs
 * between them lives here, keyed by the same slug the route uses — objectives,
 * the walkthrough video, and which stories, articles, gallery items and
 * documents that programme surfaces.
 *
 * Indices point into the shared content pools in `resources.ts`, standing in for
 * the CMS relations until the two apps share an API.
 */

import { ARTICLES, STORIES, DOWNLOADS, GALLERY, DOC_COLLECTIONS, type DocCollection } from './resources';
import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';

export interface FocusObjective {
    title: string;
    body: string;
}

export interface FocusVideo {
    heading: string;
    copy: string;
    /** Poster frame. */
    thumbnail: string;
    /** Embed URL. Absent until one is attached in the CMS. */
    url?: string;
}

/** What a programme has attached, resolved against the CMS pools. */
export interface FocusAttachments {
    stories: ArticleItem[];
    articles: ArticleItem[];
    gallery: GalleryItem[];
    documents: Record<DocCollection, DownloadItem[]>;
}

export interface FocusContent {
    objectives: FocusObjective[];
    video: FocusVideo;
    /** Bundled sample relations, as indices into the pools — the offline fallback. */
    storyIndices: number[];
    articleIndices: number[];
    galleryIndices: number[];
    documentIndices: number[];
    /** Set once the CMS has answered; from then on it alone decides what a page shows. */
    attached?: FocusAttachments;
}

const DEMO_VIDEO_IMG = '/focus/demo-video.webp';

export const FOCUS_CONTENT: Record<string, FocusContent> = {
    'tech-for-justice': {
        objectives: [
            {
                title: 'Build tools that outlast us',
                body: 'Release what we build as open, documented systems that courts, clinics and partner organisations can adopt directly — so the capability stays in the sector rather than inside one organisation.',
            },
            {
                title: 'Answer the question before it needs a lawyer',
                body: 'Most legal questions are asked at a counter that has closed, or to a relative who is guessing. Plain-language guidance, available at the hour the question is actually asked, resolves most of them without escalation.',
            },
            {
                title: 'Escalate honestly',
                body: 'Anything that needs counsel is handed to counsel with the context already gathered. Nothing we build presents a generated answer as legal advice, or predicts the outcome of a case.',
            },
            {
                title: 'Ship narrow, not clever',
                body: 'One tool, one workflow, one registry. A prototype that does a single job a partner is already obliged to do beats a platform nobody has time to adopt.',
            },
        ],
        video: {
            heading: 'Don’t just take our word for it – take it for a spin!',
            copy: 'Everything on this page is already running somewhere — in a magistrate court in the morning, in a community hall in the afternoon, in a dataset published at the end of the quarter. The quickest way to understand it is to watch a case move through it, from the first request to the day it is finally heard.',
            thumbnail: DEMO_VIDEO_IMG,
        },
        storyIndices: [3, 1],
        articleIndices: [3, 4, 5, 9],
        galleryIndices: [2, 7, 0, 9],
        documentIndices: [10, 7, 9, 0],
    },

    'access-to-justice': {
        objectives: [
            {
                title: 'Shorten the distance to a lawyer',
                body: 'Cut the time between someone needing legal help and a qualified person taking their file from months to days, whether the request comes through a phone, a partner organisation or a community paralegal.',
            },
            {
                title: 'Reach the people the scheme currently misses',
                body: 'Almost nobody finds free representation through an official channel. We recruit intake partners from the networks people already trust, because a scheme that relies on word of mouth reaches only the well connected.',
            },
            {
                title: 'Move the file, not just the case',
                body: 'The largest driver of time in pre-trial detention is not the allegation. It is the absence of anyone with the standing to ask where the docket is. Our duty solicitors pull files rather than wait to be called.',
            },
            {
                title: 'Report completions, not enquiries',
                body: 'How many people started a request tells you nothing. We publish how many finished one, and whether the people finishing look like the people the scheme was designed for.',
            },
        ],
        video: {
            heading: 'Don’t just take our word for it – take it for a spin!',
            copy: 'A duty solicitor’s morning is mostly paperwork: requesting dockets, establishing what has not been filed, and applying. Watch a matter move from a station visit to a listing, and see where the weeks actually go — and which of them were never necessary.',
            thumbnail: DEMO_VIDEO_IMG,
        },
        storyIndices: [0, 1, 3],
        articleIndices: [0, 3, 2, 6],
        galleryIndices: [4, 1, 5, 8],
        documentIndices: [7, 10, 2, 4],
    },

    'judicial-accountability': {
        objectives: [
            {
                title: 'Make delay visible',
                body: 'Publish how long each court actually takes, using registry data and freedom-of-information requests. Where a court declines to release its records, we report that refusal rather than filling the gap with an estimate.',
            },
            {
                title: 'Let the state publish its own numbers',
                body: 'The dashboard belongs to the Ministry of Justice, under its own name. That is what makes the figures defensible internally, and what makes them worth acting on rather than arguing with.',
            },
            {
                title: 'Agree what a registry should publish',
                body: 'Two courts can both be right about their disposal rate and mean different things by it. A minimum publishable standard — narrow enough to need no new record-keeping — is what makes the comparison honest.',
            },
            {
                title: 'Turn findings into reform',
                body: 'Take what the data shows to the people who can act on it: chief judges, rules committees, legislators and the press. Evidence that never reaches a decision-maker has not finished its job.',
            },
        ],
        video: {
            heading: 'Don’t just take our word for it – take it for a spin!',
            copy: 'Justice Clock counts every day a case waits and publishes the total where it cannot be ignored. Watch the data move from a registry drawer to a public dashboard, and see what a state does with its own numbers once they are in the open.',
            thumbnail: DEMO_VIDEO_IMG,
        },
        storyIndices: [1, 2],
        articleIndices: [1, 2, 7, 9],
        galleryIndices: [0, 5, 9, 6],
        documentIndices: [0, 11, 5, 9],
    },

    'democratic-governance': {
        objectives: [
            {
                title: 'Make public power answerable in practice',
                body: 'Rights are only real when institutions can be held to them. We work with citizens, civic partners and the press to test whether a stated obligation survives contact with the counter it is meant to be honoured at.',
            },
            {
                title: 'Bring the desk to the question',
                body: 'A trader cannot close a stall to attend a hearing four minutes away. Distance is never the barrier; a lost trading day is. So the clinic runs in the market, for two days at a time.',
            },
            {
                title: 'Teach the clause, not just the case',
                body: 'Reviewing agreements one at a time does not scale. A trader who can point at the clause that will cost them and say what it does will not need us for the next one.',
            },
            {
                title: 'Track renegotiations, not attendance',
                body: 'A full room is not an outcome. We count how many people went back and changed a term, walked away from a renewal, or refused a notice period they had previously accepted.',
            },
        ],
        video: {
            heading: 'Don’t just take our word for it – take it for a spin!',
            copy: 'A rights helpdesk is an unglamorous thing: a table, two days, and forty matters that would otherwise have hardened into court cases. Watch a clinic run from set-up to the contract-reading session that closes it.',
            thumbnail: DEMO_VIDEO_IMG,
        },
        storyIndices: [3, 0],
        articleIndices: [4, 6, 3],
        galleryIndices: [4, 8, 1],
        documentIndices: [7, 10, 6],
    },

    'sgbv': {
        objectives: [
            {
                title: 'Screen at the first contact',
                body: 'The escalation that precedes serious harm is usually visible in advance and recorded nowhere. A structured screening that takes six minutes gets done at a counter with a queue; one that takes twenty does not.',
            },
            {
                title: 'Attach a referral to every elevated result',
                body: 'A risk picture with no named next step is an observation. The output of a screening is a person to call, not a score presented as a verdict — the decision stays with the responder.',
            },
            {
                title: 'Hold the case through the handoffs',
                body: 'Most cases fail long before trial, in the gaps between a station, a prosecutor’s desk and a queue. Each handoff is defensible on its own terms; none of them has a clock attached. We ask, in writing, where the file is.',
            },
            {
                title: 'Keep every record inside consent',
                body: 'Nothing gathered at intake travels further than the consent it was taken under. That constraint shapes what we build, what we publish, and what we decline to record at all.',
            },
        ],
        video: {
            heading: 'Don’t just take our word for it – take it for a spin!',
            copy: 'IRIS is a short structured screening built on the indicators that actually predict escalation. Watch a responder work through it, and see where the referral route attaches — and what the tool deliberately refuses to decide.',
            thumbnail: DEMO_VIDEO_IMG,
        },
        storyIndices: [2],
        articleIndices: [8, 2, 0],
        galleryIndices: [3, 6, 7],
        documentIndices: [8, 4, 2],
    },
};

/** A programme the CMS added: nothing attached until an editor attaches it. */
export const blankFocusContent = (): FocusContent => ({
    objectives: [],
    video: { heading: '', copy: '', thumbnail: DEMO_VIDEO_IMG },
    storyIndices: [],
    articleIndices: [],
    galleryIndices: [],
    documentIndices: [],
    attached: {
        stories: [],
        articles: [],
        gallery: [],
        documents: { publications: [], reports: [], infographics: [] },
    },
});

/** Falls back to the Tech For Justice shape so a new slug never renders empty. */
export const getFocusContent = (slug?: string): FocusContent =>
    (slug && FOCUS_CONTENT[slug]) || FOCUS_CONTENT['tech-for-justice'];

/* ── Relation helpers ─────────────────────────────────────────────────── */

const pick = <T>(pool: T[], indices: number[]): T[] =>
    pool.length ? indices.map((i) => pool[i % pool.length]).filter((v, i, all) => all.indexOf(v) === i) : [];

export const focusStories = (c: FocusContent): ArticleItem[] => c.attached?.stories ?? pick(STORIES, c.storyIndices);
export const focusArticles = (c: FocusContent): ArticleItem[] =>
    c.attached?.articles ?? pick(ARTICLES, c.articleIndices);
export const focusGallery = (c: FocusContent): GalleryItem[] => c.attached?.gallery ?? pick(GALLERY, c.galleryIndices);

/**
 * Documents, per collection. The bundled sample is one flat list, so offline
 * each tab takes a slice of it; the CMS attaches per collection.
 */
export const focusDocuments = (c: FocusContent): Record<DocCollection, DownloadItem[]> => {
    if (c.attached) return c.attached.documents;
    const docs = pick(DOWNLOADS, c.documentIndices);
    const perTab = Math.max(1, Math.ceil(docs.length / DOC_COLLECTIONS.length));
    return Object.fromEntries(
        DOC_COLLECTIONS.map((collection, i) => [collection, docs.slice(i * perTab, i * perTab + perTab)]),
    ) as Record<DocCollection, DownloadItem[]>;
};
